import { ReactRenderer } from '@tiptap/react'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import type { Editor } from '@tiptap/core'
import type { EditorState } from 'prosemirror-state'
import ActionCommandSuggestionList from './ActionCommandSuggestionList'
import { ActionCommandAutocompleteItem } from './actionCommandAutocompleteSource'
import { focusActionTokenPromptNowAndAfterRender } from './actionTokenPromptFocus'
import { getSuffixPromptRange } from './actionCommandPromptRange'
import { rangeIsInsideCodeLiteral } from './codeLiteralContext'

const ACTION_COMMAND_TRIGGER_PATTERN = /(?:^|[\s\n])!([A-Za-z0-9_.-]*)$/
const ACTION_PROMPT_SENTINEL = '\u00a0'

type ActionTokenAttrs = {
    tag?: string | null
    commandText?: string | null
    promptPlaceholder?: boolean
    supportsPromptInput?: boolean
    [key: string]: unknown
}

type ActionCommandSelection = ActionCommandAutocompleteItem & {
    focusPromptOnInsert?: boolean
}

type ActionCommandSuggestionOptions = {
    usePreviousTextAsDefaultPrompt?: boolean
    wrapPreviousTextAsPrompt?: boolean
}

const extractActionCommandQuery = (textBeforeCursor: string): string | null => {
    const triggerMatch = textBeforeCursor.match(ACTION_COMMAND_TRIGGER_PATTERN)
    if (!triggerMatch) return null
    return (triggerMatch[1] || '').toLowerCase()
}

const positionIsInsideActionToken = (state: EditorState, position: number): boolean => {
    const docSize = state.doc.content.size
    const resolvedPosition = state.doc.resolve(Math.max(0, Math.min(position, docSize)))

    for (let depth = resolvedPosition.depth; depth > 0; depth--) {
        if (resolvedPosition.node(depth).type.name === 'actionTag') {
            return true
        }
    }

    return false
}

const rangeIsInsideActionToken = (
    state: EditorState,
    range?: { from: number; to: number } | null,
): boolean => {
    const from = range?.from ?? state.selection.from
    const to = range?.to ?? state.selection.to

    return (
        positionIsInsideActionToken(state, from) ||
        positionIsInsideActionToken(state, Math.max(from, to - 1))
    )
}

const fuzzyMatch = (text: string, query: string): boolean => {
    if (text.includes(query)) return true

    let queryIndex = 0
    for (let i = 0; i < text.length && queryIndex < query.length; i++) {
        if (text[i] === query[queryIndex]) {
            queryIndex++
        }
    }

    return queryIndex === query.length
}

const normalizeForLooseMatch = (text: string): string => {
    return text.replace(/[\-_]/g, '')
}

const tokenActionId = (attrs: ActionTokenAttrs | null | undefined): string | null => {
    const actionId =
        typeof attrs?.ai_action_id === 'string'
            ? attrs.ai_action_id
            : typeof attrs?.actionId === 'string'
            ? attrs.actionId
            : null
    return actionId?.trim() || null
}

const actionTokenPromptContent = (attrs: ActionTokenAttrs | null | undefined): string => {
    return typeof attrs?.promptContent === 'string'
        ? attrs.promptContent
              .replace(new RegExp(ACTION_PROMPT_SENTINEL, 'g'), '')
              .replace(/\u200b/g, '')
              .replace(/\u00a0/g, ' ')
              .replace(/[\r\n]+/g, ' ')
              .trim()
        : ''
}

const actionTokenEditorPromptText = (attrs: ActionTokenAttrs | null | undefined): string => {
    if (attrs?.promptPlaceholder === true) return ACTION_PROMPT_SENTINEL
    const promptContent = actionTokenPromptContent(attrs)
    if (promptContent) return `${promptContent}${ACTION_PROMPT_SENTINEL}`
    return attrs?.supportsPromptInput === false ? '' : ACTION_PROMPT_SENTINEL
}

const actionTokenContentForAttrs = (attrs: ActionTokenAttrs) => {
    const promptContent = actionTokenPromptContent(attrs)
    const editorPromptText = actionTokenEditorPromptText(attrs)
    const nodeAttrs = {
        ...attrs,
        ...(promptContent ? { promptContent } : {}),
    }
    if (!promptContent) {
        delete nodeAttrs.promptContent
    }
    const actionNode = {
        type: 'actionTag',
        attrs: nodeAttrs,
        ...(editorPromptText ? { content: [{ type: 'text', text: editorPromptText }] } : {}),
    }
    return [actionNode]
}

const selectionSupportsPromptInput = (
    selection: ActionCommandSelection,
    attrs: ActionTokenAttrs | null | undefined,
) => selection.supportsPromptInput !== false && attrs?.supportsPromptInput !== false

const findLastActionToken = (
    editor: Editor,
    attrs: ActionTokenAttrs | null | undefined,
): {
    pos: number
    node: { nodeSize: number; attrs: ActionTokenAttrs; textContent?: string }
} | null => {
    const actionId = tokenActionId(attrs)
    let match: {
        pos: number
        node: { nodeSize: number; attrs: ActionTokenAttrs; textContent?: string }
    } | null = null

    editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== 'actionTag') return true
        const nodeActionId = tokenActionId(node.attrs as ActionTokenAttrs)
        if (!actionId || nodeActionId === actionId) {
            match = { pos, node }
        }
        return false
    })

    return match
}

const focusInsertedActionTokenPrompt = (
    editor: Editor,
    attrs: ActionTokenAttrs | null | undefined,
) => {
    const focusPrompt = () => {
        const match = findLastActionToken(editor, attrs)
        if (!match) return
        const promptContent = actionTokenPromptContent({
            promptContent: match.node.textContent || match.node.attrs.promptContent,
        })
        const targetPos = match.pos + 1 + promptContent.length
        editor.chain().focus().setTextSelection(targetPos).run()
    }

    focusActionTokenPromptNowAndAfterRender(focusPrompt)
}

const focusAfterInsertedActionToken = (
    editor: Editor,
    insertFrom: number,
    attrs: ActionTokenAttrs | null | undefined,
) => {
    const focusAfter = () => {
        const match = findLastActionToken(editor, attrs)
        let targetPos = Math.min(insertFrom + 2, editor.state.doc.content.size)
        if (match) {
            const afterPrompt = match.pos + match.node.nodeSize
            const nextChar = editor.state.doc.textBetween(
                afterPrompt,
                Math.min(afterPrompt + 1, editor.state.doc.content.size),
                '',
                '',
            )
            targetPos = Math.min(
                nextChar === ' ' ? afterPrompt + 1 : afterPrompt,
                editor.state.doc.content.size,
            )
        }
        editor.chain().focus().setTextSelection(targetPos).run()
    }

    window.requestAnimationFrame(focusAfter)
}

const rankTextMatch = (text: string, query: string): number => {
    if (!text || !query) return Number.POSITIVE_INFINITY

    if (text.includes(query)) return 0

    const normalizedText = normalizeForLooseMatch(text)
    const normalizedQuery = normalizeForLooseMatch(query)
    if (normalizedQuery && normalizedText.includes(normalizedQuery)) return 1

    if (fuzzyMatch(text, query)) return 2

    return Number.POSITIVE_INFINITY
}

const rankCommandMatch = (item: ActionCommandAutocompleteItem, query: string): number => {
    const tag = item.tag || ''
    const lowerTag = tag.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const lowerHumanName = (item.humanName || '').toLowerCase()
    const lowerDescription = (item.descriptionShort || '').toLowerCase()

    if (!lowerQuery) return 0
    if (lowerTag === lowerQuery) return 0
    if (lowerTag.startsWith(lowerQuery)) return 1

    const humanNameRank = rankTextMatch(lowerHumanName, lowerQuery)
    if (humanNameRank !== Number.POSITIVE_INFINITY) return 2 + humanNameRank

    if (lowerTag.includes(lowerQuery)) return 6

    const normalizedTag = normalizeForLooseMatch(lowerTag)
    const normalizedQuery = normalizeForLooseMatch(lowerQuery)
    if (normalizedQuery && normalizedTag.includes(normalizedQuery)) return 7
    if (fuzzyMatch(lowerTag, lowerQuery)) return 8

    const descriptionRank = rankTextMatch(lowerDescription, lowerQuery)
    if (descriptionRank !== Number.POSITIVE_INFINITY) return 12 + descriptionRank

    return Number.POSITIVE_INFINITY
}

export const createActionCommandSuggestion = (
    getItemsFn: () => ActionCommandAutocompleteItem[],
    getDefaultActionTokenAttrs?: (
        tag: string,
        item?: ActionCommandAutocompleteItem,
    ) => ActionTokenAttrs | null | undefined,
    options: ActionCommandSuggestionOptions = {},
) => {
    const usePreviousTextAsDefaultPrompt = options.usePreviousTextAsDefaultPrompt === true
    const wrapPreviousTextAsPrompt = options.wrapPreviousTextAsPrompt === true

    return {
        items: ({ query, editor }: { query: string; editor: Editor }) => {
            const allItems = getItemsFn()
            const { state } = editor
            const { $from } = state.selection
            const textBefore = state.doc.textBetween(Math.max(0, $from.pos - 80), $from.pos, '', '')

            const extractedQuery = extractActionCommandQuery(textBefore)
            if (extractedQuery === null) return []
            const actualQuery = extractedQuery || (query ?? '').toLowerCase()

            return allItems
                .map(item => ({ item, rank: rankCommandMatch(item, actualQuery) }))
                .filter(entry => entry.rank !== Number.POSITIVE_INFINITY)
                .sort((a, b) => {
                    if (a.rank !== b.rank) return a.rank - b.rank
                    if (a.item.tag.length !== b.item.tag.length) {
                        return a.item.tag.length - b.item.tag.length
                    }
                    return a.item.tag.localeCompare(b.item.tag)
                })
                .slice(0, 12)
                .map(entry => entry.item)
        },

        render: () => {
            let component: ReactRenderer
            let popup: TippyInstance[] | undefined

            return {
                onStart: (props: SuggestionProps<ActionCommandAutocompleteItem>) => {
                    component = new ReactRenderer(ActionCommandSuggestionList, {
                        props,
                        editor: props.editor,
                    })
                    component.element.setAttribute(
                        'data-suggestion-editor',
                        props.editor.instanceId,
                    )

                    if (!props.clientRect) return

                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                        zIndex: 10000,
                    })
                },

                onUpdate(props: SuggestionProps<ActionCommandAutocompleteItem>) {
                    component?.updateProps(props)

                    if (!props.clientRect) return

                    popup?.[0]?.setProps({
                        getReferenceClientRect: props.clientRect,
                    })
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                    const event = props.event as KeyboardEvent

                    if (event.key === 'Escape') {
                        popup?.[0]?.hide()
                        return true
                    }

                    return (
                        (
                            component?.ref as {
                                onKeyDown?: (props: SuggestionKeyDownProps) => boolean
                            }
                        )?.onKeyDown?.(props) || false
                    )
                },

                onExit() {
                    popup?.[0]?.destroy()
                    component?.destroy()
                },
            }
        },

        char: '!',
        allowSpaces: false,
        allowedPrefixes: null,
        startOfLine: false,
        allow: ({ state, range }: { state: EditorState; range: { from: number; to: number } }) => {
            if (!state) return false
            if (rangeIsInsideCodeLiteral(state, range)) return false
            if (rangeIsInsideActionToken(state, range)) return false
            const selectionPos = state.selection?.$from?.pos
            if (typeof selectionPos !== 'number') return false
            const textBefore = state.doc.textBetween(
                Math.max(0, selectionPos - 80),
                selectionPos,
                '',
                '',
            )
            return extractActionCommandQuery(textBefore) !== null
        },

        command: ({
            editor,
            range,
            props,
        }: {
            editor: Editor
            range: { from: number; to: number }
            props: ActionCommandSelection
        }) => {
            const actionTokenAttrs = getDefaultActionTokenAttrs?.(props.tag, props)
            const supportsPromptInput = selectionSupportsPromptInput(props, actionTokenAttrs)
            const previousTextDefaultPromptRange =
                actionTokenAttrs && supportsPromptInput && usePreviousTextAsDefaultPrompt
                    ? getSuffixPromptRange(editor.state, range)
                    : null
            const wrappedPromptRange =
                wrapPreviousTextAsPrompt && actionTokenAttrs && supportsPromptInput
                    ? getSuffixPromptRange(editor.state, range)
                    : null
            const capturedPromptRange = previousTextDefaultPromptRange || wrappedPromptRange
            const insertRange = wrappedPromptRange
                ? { from: wrappedPromptRange.from, to: range.to }
                : range
            const nextActionTokenAttrs =
                actionTokenAttrs && capturedPromptRange
                    ? {
                          ...actionTokenAttrs,
                          promptContent: capturedPromptRange.promptContent,
                          promptPlacement: 'before',
                          ...(previousTextDefaultPromptRange ? { promptPlaceholder: true } : {}),
                      }
                    : actionTokenAttrs
            editor
                .chain()
                .focus()
                .deleteRange(insertRange)
                .insertContent(
                    nextActionTokenAttrs
                        ? [
                              ...actionTokenContentForAttrs(nextActionTokenAttrs),
                              { type: 'text', text: ' ' },
                          ]
                        : [
                              {
                                  type: 'text',
                                  text: `!${props.tag}`,
                                  marks: [{ type: 'bold' }],
                              },
                              { type: 'text', text: ' ' },
                          ],
                )
                .run()

            if (wrappedPromptRange && nextActionTokenAttrs) {
                focusAfterInsertedActionToken(editor, insertRange.from, nextActionTokenAttrs)
                return
            }

            if (props.focusPromptOnInsert && nextActionTokenAttrs && supportsPromptInput) {
                focusInsertedActionTokenPrompt(editor, nextActionTokenAttrs)
            }
        },
    }
}
